// Growth › Templates: one kind. Any template is a campaign step or a one-off
// email from a signup's profile or a tenant's Email tab. The follow-up after
// signup lives on Growth › Self-signups (owner, November 4).

import { api } from '@/api'
import type { GrowthTemplate } from '@/api/growthCampaignTypes'
import { PageHeader } from '@/components/shell/PageHeader'
import { Badge, ButtonLink, Card, DataTable, EmptyState, ErrorState, PlusIcon, RelativeTime, SkeletonRows, type Column } from '@/components/ui'
import { useDocumentTitle } from '@/lib/hooks'
import { useQuery } from '@/lib/query'

const COLUMNS: Column<GrowthTemplate>[] = [
  {
    id: 'name',
    header: 'Template',
    primary: true,
    cell: (t) => (
      <span className="grid min-w-0 gap-0.5">
        <span className="break-words font-semibold">{t.name}</span>
        <span className="break-words text-xs font-medium text-muted">{t.subject}</span>
      </span>
    ),
    className: 'min-w-[14rem]',
  },
  { id: 'check', header: 'Check', cell: (t) => (t.check.ok ? <Badge tone="good">Passes</Badge> : <Badge tone="bad">Needs work</Badge>) },
  {
    id: 'used',
    header: 'Used by',
    cell: (t) => (
      <span className="text-muted">{t.used_by === 0 ? 'No campaign' : `${t.used_by} ${t.used_by === 1 ? 'campaign' : 'campaigns'}`}</span>
    ),
  },
  { id: 'updated', header: 'Changed', cell: (t) => <RelativeTime value={t.updated_at} className="text-muted" /> },
]

export default function TemplatesListPage() {
  useDocumentTitle('Templates')
  const { data, error, status, refetch } = useQuery(['growth', 'templates'], (signal) => api.growth.templates.list(signal))
  const rows = data ?? []

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        title="Templates"
        description="Plain and personal, with one link. Use any of them in a campaign or a one-off email."
        actions={
          <ButtonLink to="/growth/templates/new" variant="primary" icon={<PlusIcon size={15} />}>
            New template
          </ButtonLink>
        }
      />
      <Card flush className="overflow-hidden">
        {status === 'loading' ? (
          <SkeletonRows rows={4} label="Loading templates" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load templates" onRetry={refetch} />
        ) : rows.length === 0 ? (
          <EmptyState title="No templates yet" description="Write one, then use it in a campaign or a one-off email." action={<ButtonLink to="/growth/templates/new">New template</ButtonLink>} />
        ) : (
          <DataTable caption="Templates" rows={rows} columns={COLUMNS} rowKey={(t) => t.id} rowHref={(t) => `/growth/templates/${t.id}`} />
        )}
      </Card>
    </div>
  )
}
